import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { ATLAS_SAVED_VIEW_DEFINITION_VERSION, type AtlasSavedViewDefinition } from '../models/AtlasSavedView';
import { getActiveFilterRules, type ViewRule } from '../../../core/assistant/models/ViewRules';

/**
 * `source`: the caller can read the source chart and receives the definition.
 * `scene`: the caller holds only the saved-view diagram's grant and must render
 * the server-filtered scene instead of issuing folder reads.
 */
export type AtlasSavedViewRead =
  | {
    readonly access: 'source';
    readonly id: string;
    readonly diagramId: string;
    readonly definition: AtlasSavedViewDefinition;
    readonly revision: number;
    /** Diagram write plus source Full Org Chart write — the server's update gate. */
    readonly canEditDefinition?: boolean;
  }
  | {
    readonly access: 'scene';
    readonly id: string;
    readonly diagramId: string;
    readonly revision: number;
  };

export type AtlasSavedViewSourceRead = Extract<AtlasSavedViewRead, { access: 'source' }>;

/** The department key implied by a `?dept=` lens, added as its own always-on rule. */
const DEPARTMENT_LENS_RULE_ID = 'department-lens';

/** The outbound shape of a filter rule — a plain interface, not the server's
 * passthrough-validated type, since these are values we're about to send,
 * not ones we've received and need to satisfy the exact Zod inference for. */
export type AtlasFilterRuleInput = Pick<ViewRule, 'id' | 'name' | 'conditions' | 'logic' | 'enabled' | 'order'>;

/** Enabled filter-kind rules with actual conditions, in the persisted shape. */
export function toAtlasFilterRules(rules: readonly ViewRule[]): AtlasFilterRuleInput[] {
  return getActiveFilterRules([...rules]).map((rule) => ({
    id: rule.id, name: rule.name, conditions: rule.conditions, logic: rule.logic,
    enabled: rule.enabled, order: rule.order,
  }));
}

export function useCreateAtlasSavedView(folderId: string | null, sourceDiagramId: string | null) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; rules: readonly ViewRule[]; departmentKey?: string | null }) => {
      if (!folderId || !sourceDiagramId) throw new Error('The source chart is unavailable.');
      const filterRules = toAtlasFilterRules(input.rules);
      if (input.departmentKey) {
        filterRules.push({
          id: DEPARTMENT_LENS_RULE_ID, name: 'Department', logic: 'and', enabled: true, order: filterRules.length,
          conditions: [{ id: DEPARTMENT_LENS_RULE_ID, field: 'departmentKey', operator: 'equals', value: input.departmentKey }],
        });
      }
      const response = await authenticatedFetch(`/api/folders/${encodeURIComponent(folderId)}/atlas/saved-views`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sourceDiagramId, name: input.name, idempotencyKey: crypto.randomUUID(), definition: {
          version: ATLAS_SAVED_VIEW_DEFINITION_VERSION, filterRules, includeReportingChain: true,
        } }),
      });
      if (!response.ok) throw new Error(`Could not save custom view (${response.status})`);
      return response.json() as Promise<{ diagramId: string; redirect: string }>;
    },
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['dashboard'] }); },
  });
}

export class AtlasSavedViewConflictError extends Error {
  constructor() { super('saved_view_conflict'); }
}

/**
 * Direct-edit autosave target for a saved view's filters: PATCHes the
 * definition with the caller's last-known revision. A 409 surfaces as
 * `AtlasSavedViewConflictError` — the caller (a debounced write, never a
 * user-facing form submit) is expected to refetch and resume from there,
 * not show an error.
 */
export function useUpdateAtlasSavedView(folderId: string | null, savedViewId: string | null) {
  const authenticatedFetch = useAuthenticatedFetch();
  return useMutation({
    mutationFn: async (input: { filterRules: AtlasFilterRuleInput[]; expectedRevision: number }) => {
      if (!folderId || !savedViewId) throw new Error('The saved view is unavailable.');
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/atlas/saved-views/${encodeURIComponent(savedViewId)}`,
        {
          method: 'PATCH', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            expectedRevision: input.expectedRevision,
            definition: {
              version: ATLAS_SAVED_VIEW_DEFINITION_VERSION,
              filterRules: input.filterRules,
              includeReportingChain: true,
            },
          }),
        },
      );
      if (response.status === 409) throw new AtlasSavedViewConflictError();
      if (!response.ok) throw new Error(`Could not update custom view (${response.status})`);
      return response.json() as Promise<{ revision: number }>;
    },
  });
}

/** Reads a definition only for its bound diagram; it is never copied into Yjs. */
export function useAtlasSavedView(
  folderId: string | null,
  savedViewId: string | null,
  options?: { enabled?: boolean },
) {
  const authenticatedFetch = useAuthenticatedFetch();
  return useQuery({
    queryKey: ['atlas', 'saved-view', folderId, savedViewId],
    enabled: Boolean(folderId && savedViewId && (options?.enabled ?? true)),
    retry: false,
    queryFn: async ({ signal }): Promise<AtlasSavedViewRead> => {
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId!)}/atlas/saved-views/${encodeURIComponent(savedViewId!)}`,
        { signal },
      );
      if (!response.ok) throw new Error(`Saved Atlas view is unavailable (${response.status})`);
      return response.json() as Promise<AtlasSavedViewRead>;
    },
  });
}
